import React, { useMemo } from "react";
import dayjs from "dayjs";
import cn from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "@helpers/translate";
import { getFullMonthLabel } from "@utils/fullMonthLabels";
import ArrowDownIcon from "@assets/icons/ArrowDownIcon";
import { getWeekdayLabel } from "@utils/daysLabels";

import {
  buildMonthGrid,
  buildYearOptions,
  CALENDAR_MONTH_INDEXES,
  CALENDAR_YEAR_MAX,
  CALENDAR_YEAR_MIN,
  formatMonthYearTitle,
  getRangeCellKind,
} from "./transactionDateRangePicker.helpers";

/**
 * @param {{ viewMonth: import("dayjs").Dayjs, pendingFrom: import("dayjs").Dayjs | null, pendingTo: import("dayjs").Dayjs | null, onDayClick: (d: import("dayjs").Dayjs) => void }} props
 */
export function DayCalendarView({
  viewMonth,
  pendingFrom,
  pendingTo,
  onDayClick,
}) {
  const { __ } = useTranslation();
  const WEEKDAYS = Array.from({ length: 7 }, (_, i) => i);
  const cells = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const todayStart = dayjs().startOf("day");

  return (
    <>
      <div className="tx-date-range-picker__weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w} className="tx-date-range-picker__weekday">
            {getWeekdayLabel(w, __)}
          </div>
        ))}
      </div>

      <div className="tx-date-range-picker__grid">
        {cells.map(({ date, inCurrentMonth }) => {
          const rangeKind = getRangeCellKind(date, pendingFrom, pendingTo);
          const isEndpoint = rangeKind === "endpoint";
          const isBetween = rangeKind === "between";
          const d = date.startOf("day");
          const isToday =
            d.isSame(todayStart, "day") && !isEndpoint && !isBetween;

          return (
            <button
              key={date.valueOf()}
              type="button"
              className={cn("tx-date-range-picker__cell", {
                "tx-date-range-picker__cell--muted": !inCurrentMonth,
                "tx-date-range-picker__cell--between": isBetween,
                "tx-date-range-picker__cell--edge": isEndpoint,
                "tx-date-range-picker__cell--today": isToday,
              })}
              aria-current={isToday ? "date" : undefined}
              onClick={() => onDayClick(date)}
            >
              <span className="tx-date-range-picker__cell-inner">
                {date.date()}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * @param {{ selectedMonthIndex: number, onSelectMonth: (monthIndex: number) => void }} props
 */
export function MonthSelectionView({ selectedMonthIndex, onSelectMonth }) {
  const { __ } = useTranslation();

  return (
    <div className="tx-date-range-picker__meta-grid tx-date-range-picker__meta-grid--months">
      {CALENDAR_MONTH_INDEXES.map((mi) => (
        <button
          key={mi}
          type="button"
          className={cn("tx-date-range-picker__meta-cell", {
            "tx-date-range-picker__meta-cell--selected":
              mi === selectedMonthIndex,
          })}
          onClick={() => onSelectMonth(mi)}
        >
          {getFullMonthLabel(mi, __)}
        </button>
      ))}
    </div>
  );
}

/**
 * @param {{ activeYear: number, onSelectYear: (year: number) => void }} props
 */
export function YearSelectionView({ activeYear, onSelectYear }) {
  const years = useMemo(() => buildYearOptions(), []);

  return (
    <div className="tx-date-range-picker__meta-grid tx-date-range-picker__meta-grid--years">
      {years.map((y) => (
        <button
          key={y}
          type="button"
          className={cn("tx-date-range-picker__meta-cell", {
            "tx-date-range-picker__meta-cell--selected": y === activeYear,
          })}
          onClick={() => onSelectYear(y)}
        >
          {y}
        </button>
      ))}
    </div>
  );
}

/**
 * @typedef {Object} CalendarPanelState
 * @property {"days" | "months" | "years"} viewMode
 * @property {import("dayjs").Dayjs} viewMonth start of month
 */

/**
 * @param {{
 *   panel: CalendarPanelState,
 *   setPanel: React.Dispatch<React.SetStateAction<CalendarPanelState>>,
 *   pendingFrom: import("dayjs").Dayjs | null,
 *   pendingTo: import("dayjs").Dayjs | null,
 *   onDayClick: (d: import("dayjs").Dayjs) => void,
 *   showNav: boolean,
 *   minNavMonth: import("dayjs").Dayjs,
 *   maxNavMonth: import("dayjs").Dayjs,
 * }} props
 */
export function CalendarPanel({
  panel,
  setPanel,
  pendingFrom,
  pendingTo,
  onDayClick,
  showNav,
  minNavMonth,
  maxNavMonth,
}) {
  const { __ } = useTranslation();
  const { viewMode, viewMonth } = panel;

  const monthYearTitle = formatMonthYearTitle(viewMonth, __);

  const canPrev =
    viewMode === "days" && viewMonth.isAfter(minNavMonth, "month");
  const canNext =
    viewMode === "days" && viewMonth.isBefore(maxNavMonth, "month");

  const goPrevMonth = () => {
    setPanel((p) => ({
      ...p,
      viewMonth: p.viewMonth.subtract(1, "month").startOf("month"),
    }));
  };

  const goNextMonth = () => {
    setPanel((p) => ({
      ...p,
      viewMonth: p.viewMonth.add(1, "month").startOf("month"),
    }));
  };

  const openMonthsView = () => {
    setPanel((p) => ({ ...p, viewMode: "months" }));
  };

  const openYearsView = () => {
    setPanel((p) => ({ ...p, viewMode: "years" }));
  };

  const selectMonth = (monthIndex) => {
    setPanel((p) => ({
      ...p,
      viewMode: "days",
      viewMonth: p.viewMonth.month(monthIndex).startOf("month"),
    }));
  };

  const selectYear = (year) => {
    setPanel((p) => ({
      ...p,
      viewMode: "months",
      viewMonth: p.viewMonth.year(year).startOf("month"),
    }));
  };

  return (
    <div className="tx-date-range-picker__month-block">
      {viewMode === "days" && (
        <>
          {showNav ? (
            <div className="tx-date-range-picker__nav">
              <button
                type="button"
                className="tx-date-range-picker__nav-title tx-date-range-picker__nav-title--clickable"
                onClick={openMonthsView}
              >
                {monthYearTitle}
                <ArrowDownIcon />
              </button>
              <div className="tx-date-range-picker__nav-controls">
                <button
                  type="button"
                  className="tx-date-range-picker__nav-btn"
                  onClick={goPrevMonth}
                  disabled={!canPrev}
                  aria-label="Previous month"
                >
                  <ChevronLeft strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="tx-date-range-picker__nav-btn"
                  onClick={goNextMonth}
                  disabled={!canNext}
                  aria-label="Next month"
                >
                  <ChevronRight strokeWidth={2} />
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="tx-date-range-picker__month-title tx-date-range-picker__month-title--clickable"
              onClick={openMonthsView}
            >
              {monthYearTitle} <ArrowDownIcon />
            </button>
          )}

          <DayCalendarView
            viewMonth={viewMonth}
            pendingFrom={pendingFrom}
            pendingTo={pendingTo}
            onDayClick={onDayClick}
          />
        </>
      )}

      {viewMode === "months" && (
        <div className="tx-date-range-picker__meta-block">
          <button
            type="button"
            className="tx-date-range-picker__meta-year-title"
            onClick={openYearsView}
          >
            {viewMonth.year()} <ArrowDownIcon />
          </button>
          <MonthSelectionView
            selectedMonthIndex={viewMonth.month()}
            onSelectMonth={selectMonth}
          />
        </div>
      )}

      {viewMode === "years" && (
        <div className="tx-date-range-picker__meta-block">
          <div className="tx-date-range-picker__meta-range-label" aria-hidden>
            {CALENDAR_YEAR_MIN}–{CALENDAR_YEAR_MAX}
          </div>
          <YearSelectionView
            activeYear={viewMonth.year()}
            onSelectYear={selectYear}
          />
        </div>
      )}
    </div>
  );
}
